import { useNavigate } from 'react-router';
import { Container, Box, Button, Alert, Backdrop } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { RepresentativesSection } from '../../components/representatives-section';
import { SupplierInfoSection } from '../../components/supplier/supplier-info-section';
import { CurrentAddressSection } from '../../components/current-address-section';
import { HeadOfficeAddressSection } from '../../components/head-office-address-section';
import { FactorySection } from '../../components/supplier/factory-section';
import { CertificationsSection } from '../../components/supplier/certifications-section';
import { UnionMembershipSection } from '../../components/supplier/union-membership-section';
import { QualifiedPersonnelSection } from '../../components/supplier/qualified-personnel-section';
import { RawMaterialsSection } from '../../components/supplier/raw-materials-section';
import { TransportVehiclesSection } from '../../components/supplier/transport-vehicles-section';
import { MixerSection } from '../../components/supplier/mixer-section';
import { NavigationPanel } from '../../components/supplier/navigation-panel';
import { CreatedUpdatedInfoSection } from '../../components/created-updated-info-section';
import { useScrollSpy } from '../../hooks/supplier/useScrollSpy';
import { useManualSectionPriority } from '../../hooks/supplier/use-manual-section-priority';
import { useSupplierDetailForm } from '../../hooks/supplier/use-supplier-detail-form';
import { NavigationSection } from '../../types/company.types';
import { companiesService } from '../../services/companies.service';

import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import { paths } from '@/config/paths';
import HeaderRegister from '@/features/apps/components/header-register';
import { FixedFooter } from '@/components/footer/fixed-footer';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { MESSAGES } from '@/lib/constants';
import { useSnackbar } from '@/hooks/use-snackbar';
import { checkPermission } from '@/utils/helpers';
import { FunctionName, PermissionAction } from '@/lib/enums';

const navigationSections: NavigationSection[] = [
  { id: 'company-registration', label: 'ページTOP', anchor: 'company-registration-section' },
  { id: 'address', label: '所在地', anchor: 'address-section' },
  { id: 'head-office-address', label: '本社住所', anchor: 'head-office-address-section' },
  { id: 'nohara-representative', label: '野原担当', anchor: 'representatives-section' },
  { id: 'factory', label: '工場', anchor: 'factory-section' },
  { id: 'certifications', label: '認証', anchor: 'certifications-section' },
  { id: 'union-membership', label: '加盟組合', anchor: 'union-membership-section' },
  { id: 'qualified-personnel', label: '資格取得者', anchor: 'qualified-personnel-section' },
  { id: 'raw-materials', label: '原材料', anchor: 'raw-materials-section' },
  { id: 'mixer', label: 'ミキサ', anchor: 'mixer-section' },
  { id: 'transport-vehicles', label: '運搬車', anchor: 'transport-vehicles-section' },
  { id: 'created-updated-info', label: '登録・更新情報', anchor: 'created-updated-info-section' },
];

const HEADER_OFFSET = 50;
const MANUAL_SECTION_PRIORITY_MS = 1000;

export const SupplierDetailPage = () => {
  const navigate = useNavigate();
  const { markManualSection, shouldApplySpySection } = useManualSectionPriority(
    MANUAL_SECTION_PRIORITY_MS
  );

  // Use custom hook for all form logic
  const {
    supplierId,
    formData,
    originalFormData,
    isLoading,
    error,
    isDeleting,
    activeSection,
    hasFormChanges,
    departments,
    departmentsLoading,
    staffsLoading,
    updateField,
    updateCurrentAddress,
    updateHeadOfficeAddress,
    copyCurrentToHeadOffice,
    updateFactoryPostalCode1,
    updateFactoryPostalCode2,
    updateFactoryContact,
    updateCertification,
    updateIndustrialUnion,
    updateCooperativeUnion,
    updateQualifiedPersonnel,
    updateMixerInfo,
    updateTransportVehicles,
    updateRawMaterial,
    addRawMaterial,
    removeRawMaterial,
    addCooperativeUnion,
    removeCooperativeUnion,
    addFactoryContact,
    removeFactoryContact,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    getSalesPersonOptions,
    handleDepartmentChange,
    getFieldError,
    isFieldValid,
    canAddRepresentative,
    isSubmitDisabled,
    handleSubmit,
    setActiveSection,
    setIsLoading,
  } = useSupplierDetailForm();
  const { setConfirmDialog } = useConfirmDialog();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsSubmitting } = useSubmitting();
  const { permissions } = useOidc();
  const [canUpdateSupplier, setCanUpdateSupplier] = useState<boolean>(false);
  const [canDeleteSupplier, setCanDeleteSupplier] = useState<boolean>(false);
  // Wrapper functions for compatibility with component props
  const factoryAddress = {
    factoryName: formData.factoryName,
    postalCode1: formData.factoryPostalCode1,
    postalCode2: formData.factoryPostalCode2,
    addressLocation: formData.factoryAddress,
  };
  const originalFactoryAddress = {
    factoryName: originalFormData?.factoryName,
    postalCode1: originalFormData?.factoryPostalCode1,
    postalCode2: originalFormData?.factoryPostalCode2,
    addressLocation: originalFormData?.factoryAddress,
  };

  const onUpdateFactoryAddress = (field: string, value: string) => {
    if (field === 'postalCode1') {
      updateFactoryPostalCode1(value);
    } else if (field === 'postalCode2') {
      updateFactoryPostalCode2(value);
    } else {
      const fieldMapping: Record<string, string> = {
        factoryName: 'factoryName',
        addressLocation: 'factoryAddress',
      };
      const mappedField = fieldMapping[field];
      if (mappedField) {
        updateField(mappedField as keyof typeof formData, value);
      }
    }
  };

  // Wrapper for qualified personnel - keep original value for validation
  const handleUpdateQualifiedPersonnel = (field: string, value: string | number) => {
    updateQualifiedPersonnel(field, value);
  };

  // Wrapper for transport vehicles - keep original value for validation
  const handleUpdateTransportVehicles = (field: string, value: string | number) => {
    updateTransportVehicles(field, value);
  };

  // Scroll spy for navigation
  const handleSetActiveSection = (section: string) => {
    if (!shouldApplySpySection(section)) {
      return;
    }

    setActiveSection(section);
  };
  useScrollSpy(navigationSections, handleSetActiveSection, { topOffset: HEADER_OFFSET });

  // Show popup warning when user tries to close/refresh page with unsaved changes
  useBeforeUnloadWarning(hasFormChanges);

  // Handle form submission with navigation
  const onSubmit = async () => {
    const success = await handleSubmit();
    if (success) {
      setIsSubmitting(true);
      navigate('/companies');
      setSnackbarMessage(MESSAGES.SUPPLIER_HAS_BEEN_CHANGED);
    }
  };

  const handleBackToList = () => {
    navigate(paths.companies.path);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const deleteSupplier = async (supplierId: string) => {
    setIsLoading(true);
    try {
      await companiesService.deleteSupplier(supplierId);
      setIsSubmitting(true);
      navigate(paths.companies.path);
      setSnackbarMessage(MESSAGES.DELETE_SUPPLIER);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      const message = '削除に失敗しました。もう一度お試しください。';
      setConfirmDialog({
        title: message,
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteSupplier = async () => {
    if (!supplierId) return;
    setConfirmDialog({
      title: '仕入先情報を削除します。',
      confirmDialog: () => {
        void deleteSupplier(supplierId);
      },
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
    });
  };

  const handleSectionClick = (sectionId: string) => {
    markManualSection(sectionId);
    setActiveSection(sectionId);

    const anchor = navigationSections.find((s) => s.id === sectionId)?.anchor;
    if (anchor) {
      const element = document.getElementById(anchor);
      if (element) {
        const offsetTop =
          element.getBoundingClientRect().top + globalThis.scrollY - HEADER_OFFSET - 12;
        globalThis.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }
  };

  // Check permission on mount - redirect to 404 if user doesn't have READ or UPDATE permission
  useEffect(() => {
    const canRead = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.READ
    );
    const canUpdate = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.UPDATE
    );

    if (!canRead && !canUpdate) {
      navigate('/404');
      return;
    }
  }, [permissions, navigate]);

  // Set permissions for update and delete
  useEffect(() => {
    setCanUpdateSupplier(formData.canUpdate ?? false);
    setCanDeleteSupplier(formData.canUpdate ?? false);
  }, [formData.canUpdate]);

  // Show loading error if data fetch failed
  if (error && !formData.officialName) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant='outlined' startIcon={<ArrowBackIcon />} onClick={handleBackToList}>
          会社一覧に戻る
        </Button>
      </Container>
    );
  }
  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister title='仕入先詳細' description='仕入先情報の詳細表示・編集' />

      {/* General Error Messages */}
      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Form Content */}
        <Box sx={{ flex: 1, position: 'relative' }} inert={!canUpdateSupplier ? true : false}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box
              id='company-registration-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <SupplierInfoSection
                formData={{
                  ...formData,
                  companyType: '仕入先' as const,
                  factoryAddress: factoryAddress,
                }}
                originalFormData={{
                  ...originalFormData,
                  companyType: '仕入先' as const,
                  factoryAddress: factoryAddress,
                }}
                onUpdateField={(field, value) => updateField(field as keyof typeof formData, value)}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box id='address-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}>
              <CurrentAddressSection
                currentAddress={formData.currentAddress}
                originalCurrentAddress={originalFormData?.currentAddress}
                onUpdateAddressField={updateCurrentAddress}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='head-office-address-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <HeadOfficeAddressSection
                headOfficeAddress={formData.headOfficeAddress}
                originalHeadOfficeAddress={originalFormData?.headOfficeAddress}
                onUpdateAddressField={updateHeadOfficeAddress}
                onCopyCurrentToHeadOffice={copyCurrentToHeadOffice}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='representatives-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <RepresentativesSection
                representatives={formData.representatives}
                originalRepresentative={originalFormData?.representatives}
                isEditMode={!!originalFormData}
                onAddRepresentative={addRepresentative}
                onUpdateRepresentative={updateRepresentative}
                onRemoveRepresentative={removeRepresentative}
                getSalesPersonOptions={getSalesPersonOptions}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
                canAddRepresentative={canAddRepresentative}
                departments={departments}
                departmentsLoading={departmentsLoading}
                staffsLoading={staffsLoading}
                onDepartmentChange={handleDepartmentChange}
              />
            </Box>

            <Box id='factory-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}>
              <FactorySection
                factoryAddress={factoryAddress}
                originalFactoryAddress={originalFactoryAddress}
                factoryContacts={formData.factoryContacts}
                originalFactoryContacts={originalFormData?.factoryContacts}
                isEditMode={!!originalFormData}
                onUpdateFactoryAddress={onUpdateFactoryAddress}
                onAddFactoryContact={addFactoryContact}
                onUpdateFactoryContact={updateFactoryContact}
                onRemoveFactoryContact={removeFactoryContact}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='certifications-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <CertificationsSection
                certificationInfo={formData.certificationInfo}
                originalCertificationInfo={originalFormData?.certificationInfo}
                isEditMode={!!originalFormData}
                onUpdateField={updateCertification}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='union-membership-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <UnionMembershipSection
                unionMembership={formData.unionMembership}
                originalUnionMembership={originalFormData?.unionMembership}
                isEditMode={!!originalFormData}
                onUpdateIndustrialUnion={updateIndustrialUnion}
                onAddCooperativeUnion={addCooperativeUnion}
                onUpdateCooperativeUnion={updateCooperativeUnion}
                onRemoveCooperativeUnion={removeCooperativeUnion}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='qualified-personnel-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <QualifiedPersonnelSection
                qualifiedPersonnel={formData.qualifiedPersonnel}
                originalQualifiedPersonnel={originalFormData?.qualifiedPersonnel}
                isEditMode={!!originalFormData}
                onUpdateField={handleUpdateQualifiedPersonnel}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='raw-materials-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <RawMaterialsSection
                rawMaterials={formData.rawMaterials}
                originalRawMaterials={originalFormData?.rawMaterials}
                isEditMode={!!originalFormData}
                onAddRawMaterial={addRawMaterial}
                onUpdateRawMaterial={updateRawMaterial}
                onRemoveRawMaterial={removeRawMaterial}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box id='mixer-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}>
              <MixerSection
                mixerInfo={formData.mixerInfo}
                originalMixerInfo={originalFormData?.mixerInfo}
                isEditMode={!!originalFormData}
                onUpdateField={updateMixerInfo}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='transport-vehicles-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <TransportVehiclesSection
                transportVehicles={formData.transportVehicles}
                originalTransportVehicles={originalFormData?.transportVehicles}
                isEditMode={!!originalFormData}
                onUpdateTransportVehicleField={handleUpdateTransportVehicles}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
              />
            </Box>

            <Box
              id='created-updated-info-section'
              sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
            >
              <CreatedUpdatedInfoSection
                createdAt={formData.createdAt}
                createdUser={formData.createdStaff ? formData.createdStaff : formData.createdUser}
                updatedAt={formData.updatedAt}
                updatedUser={formData.updatedStaff ? formData.updatedStaff : formData.updatedUser}
              />
            </Box>
          </Box>

          {/* Backdrop to prevent interaction when canUpdate is false */}
          <Backdrop
            open={!canUpdateSupplier}
            sx={{
              position: 'absolute',
              backgroundColor: 'rgba(0, 0, 0, 0)',
              zIndex: 9999,
            }}
          />
        </Box>

        {/* Navigation Panel */}
        <Box
          sx={{
            width: 250,
            flexShrink: 0,
            position: 'sticky',
            top: `${HEADER_OFFSET + 12}px`,
            alignSelf: 'start',
          }}
        >
          <NavigationPanel
            sections={navigationSections}
            activeSection={activeSection}
            onSectionClick={handleSectionClick}
          />
        </Box>
      </Box>

      {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
      <Box sx={{ height: '100px' }} />
      {/* Fixed Action Footer */}
      <FixedFooter
        buttons={[
          {
            label: isDeleting ? '削除中...' : '削除',
            onClick: handleDeleteSupplier,
            variant: 'outlined',
            color: 'error',
            startIcon: <DeleteIcon />,
            disabled: isLoading || isDeleting || !canDeleteSupplier,
            loading: isDeleting,
            sx: {
              color: 'red',
              borderColor: 'red',
              '&:hover': {
                borderColor: 'darkred',
                backgroundColor: 'rgba(255, 0, 0, 0.04)',
              },
            },
          },
          {
            label: 'キャンセル',
            onClick: handleCancel,
            variant: 'outlined',
            startIcon: <CancelIcon />,
            disabled: isLoading,
            sx: {
              marginLeft: 'auto',
            },
          },
          {
            label: isLoading ? '更新中...' : '更新',
            onClick: onSubmit,
            variant: 'contained',
            startIcon: <EditIcon />,
            disabled: isSubmitDisabled || !canUpdateSupplier,
            loading: isLoading,
          },
        ]}
        isSubmitting={isLoading}
      />
    </Container>
  );
};
